import { ENV_CONFIG } from 'env-config';
import { PaginationState } from 'types/custom/common';
import { SimulationSource } from 'types/custom/simulation';
const serverURL = ENV_CONFIG.albServerUrl;
const backtestingURL = ENV_CONFIG.backtestingURL;
const liveTickServerUrl = ENV_CONFIG.liveTickServerUrl;

class API {
  // GET Websocket API

  getCountry() {
    return localStorage.getItem('country');
  }

  getWebSocketAPI() {
    return ENV_CONFIG.websocketServerUrl;
  }

  //* AUTH API'S
  login(): string {
    return `${serverURL}/user/${this.getCountry()}/v1/admin/exists`;
  }

  forgotPassword(): string {
    return `${serverURL}/user/${this.getCountry()}/v1/password/forgot`;
  }

  verifyOtp(): string {
    return `${serverURL}/user/${this.getCountry()}/v1/password/verify-otp`;
  }

  resetPassword(): string {
    return `${serverURL}/user/${this.getCountry()}/v1/password/reset`;
  }

  changePassword(): string {
    return `${serverURL}/user/${this.getCountry()}/v1/password/change`;
  }

  register(): string {
    return `${serverURL}/user/${this.getCountry()}/v1/create`;
  }

  //* ADMIN API'S
  saveAdmin(): string {
    return `${serverURL}/user/${this.getCountry()}/v1/employee/create`;
  }

  //* STOCK API'S

  getAllAvailableStocks() {
    return `${serverURL}/stock/${this.getCountry()}/v1/list`;
  }

  saveStock() {
    return `${serverURL}/stock/${this.getCountry()}/v1/save`;
  }

  fetchHistoricPrices() {
    return `${serverURL}/historic-stock-price/${this.getCountry()}/v1/fetch`;
  }

  deleteHistoricPrice(stockId: string) {
    return `${serverURL}/historic-stock-price/${this.getCountry()}/v1/delete?stockId=${stockId}`;
  }

  importStock(stockName: string): string {
    return `${serverURL}/historic-stock-price/${this.getCountry()}/v1/import?stockName=${stockName}`;
  }

  getTimePeriodsOfRunningStrategy(stockId: string): string {
    return `${serverURL}/v1/trade/stock/${this.getCountry()}/period?stockId=${stockId}`;
  }

  getOhlcAndIndicatorByTimePeriod(stockId: string, timePeriod: string): string {
    return `${serverURL}/v1/trade/stock/${this.getCountry()}/candles?stockId=${stockId}&stockPeriod=${timePeriod}`;
  }

  getAllStockGroups() {
    return `${serverURL}/stock/${this.getCountry()}/v1/stock-group/all`;
  }

  saveStockGroup() {
    return `${serverURL}/stock/${this.getCountry()}/v1/stock-group/save`;
  }

  updateStockGroup(groupId: string) {
    return `${serverURL}/stock/${this.getCountry()}/v1/stock-group/update?groupId=${groupId}`;
  }

  deleteStockGroup(groupId: string) {
    return `${serverURL}/stock/${this.getCountry()}/v1/stock-group/delete?groupId=${groupId}`;
  }

  //* HEALTH CHECK API'S

  checkServerHealth(): string {
    return `${serverURL}/server-health/${this.getCountry()}/v1/check-health`;
  }

  //* SIMULATION

  startSimulation() {
    return `${serverURL}/simulation/${this.getCountry()}/v1/start`;
  }

  startBackTesting() {
    return `${backtestingURL}/back-testing/${this.getCountry()}/v1/start`;
  }
  startMultiSimulation() {
    return `${serverURL}/simulation/${this.getCountry()}/v1/start/multi`;
  }

  updateSimulationConfig() {
    return `${serverURL}/simulation/${this.getCountry()}/v1/config/update`;
  }

  stopMultiSimulation(simulationId: string) {
    return `${serverURL}/simulation/${this.getCountry()}/v1/stop/multi?simulationId=${simulationId}`;
  }

  hideSimulation(simulationId: string) {
    return `${serverURL}/simulation/${this.getCountry()}/v1/hide?simulationId=${simulationId}`;
  }

  executeInMarket(simulationId: string, executeInMarket: boolean) {
    return `${serverURL}/simulation/${this.getCountry()}/v1/execute-in-market?simulationId=${simulationId}&executeInMarket=${executeInMarket}`;
  }

  addUserToLiveTrade(simulationId: string, userId: string, description?: string) {
    const descriptionParam = description ? `&description=${encodeURIComponent(description)}` : '';
    return `${serverURL}/simulation/${this.getCountry()}/v1/add-user?simulationId=${simulationId}&userId=${userId}${descriptionParam}`;
  }
  removeUserFromLiveTrade(simulationId: string, userId: string) {
    return `${serverURL}/simulation/${this.getCountry()}/v1/remove-user?simulationId=${simulationId}&userId=${userId}`;
  }
  addSimulationAccountToLiveTrade(simulationId: string, accountName: string, startingBalance: number) {
    return `${serverURL}/simulation/${this.getCountry()}/v1/add-simulation-account?simulationId=${simulationId}&accountName=${encodeURIComponent(
      accountName
    )}&startingBalance=${startingBalance}`;
  }
  updateAccountDescription(simulationId: string, accountId: string, description: string) {
    const encodedDescription = encodeURIComponent(description);
    return `${serverURL}/simulation/${this.getCountry()}/v1/account-description?simulationId=${simulationId}&accountId=${accountId}&description=${encodedDescription}`;
  }
  saveSimulationAccountConfig(simulationId: string, accountId: string) {
    return `${serverURL}/simulation/${this.getCountry()}/v1/simulation-account/config/save?simulationId=${simulationId}&accountId=${accountId}`;
  }

  restartMultiSimulation(simulationId: string) {
    return `${serverURL}/simulation/${this.getCountry()}/v1/restart/multi?simulationId=${simulationId}`;
  }

  forceRestartSimulations() {
    return `${serverURL}/simulation/${this.getCountry()}/v1/forceRestart`;
  }

  getSimulationHistory({ page, limit }: PaginationState) {
    return `${serverURL}/simulation/${this.getCountry()}/v1/all?page=${page}&limit=${limit}`;
  }

  getLiveSimulationHIstory({ page, limit }: PaginationState) {
    return `${serverURL}/simulation/${this.getCountry()}/v1/live?page=${page}&limit=${limit}`;
  }

  getRecommendationSimulationHistory({ page, limit }: PaginationState) {
    return `${serverURL}/simulation/${this.getCountry()}/v1/recommendations?page=${page}&limit=${limit}`;
  }

  /** Which trades restart on which days. source is live or recommendation. */
  getTradeSchedules(source: 'live' | 'recommendation') {
    return `${serverURL}/trade-schedule/${this.getCountry()}/v1/list?source=${source}`;
  }

  /** PUT: schedules a trade for the given days. An empty list removes the schedule. */
  saveTradeSchedule() {
    return `${serverURL}/trade-schedule/${this.getCountry()}/v1/save`;
  }

  /** DELETE: removes a trade's schedule. */
  deleteTradeSchedule(simulationId: string) {
    return `${serverURL}/trade-schedule/${this.getCountry()}/v1/${simulationId}`;
  }

  getSimulationById(simulationId: string) {
    return `${serverURL}/simulation/${this.getCountry()}/v1/get?simulationId=${simulationId}`;
  }

  getNoTradeLog(simulationId: string) {
    return `${serverURL}/simulation/${this.getCountry()}/v1/get/no-trade-decision?simulationId=${simulationId}`;
  }

  getTodayNoTradeDecisions(stockId?: string) {
    const query = stockId ? `?stockId=${encodeURIComponent(stockId)}` : '';
    return `${serverURL}/no-trade-decision/${this.getCountry()}/v1/today${query}`;
  }

  getNoTradeOptionChain(stockId: string) {
    return `${serverURL}/no-trade-decision/${this.getCountry()}/v1/option-chain?stockId=${encodeURIComponent(stockId)}`;
  }

  getSimulationByIdV2(simulationId: string) {
    return `${serverURL}/simulation/${this.getCountry()}/v2/get?simulationId=${simulationId}`;
  }

  /**
   * Stats for a running live trade — the back-testing stats block, computed for
   * the trade's simulation (paper) account. `accountId` picks one when a trade
   * has several; omit it for the primary account.
   */
  getLiveTradeStats(simulationId: string, accountId?: string) {
    const url = `${serverURL}/simulation/${this.getCountry()}/v1/live/stats?simulationId=${simulationId}`;
    return accountId ? `${url}&accountId=${encodeURIComponent(accountId)}` : url;
  }

  getAccountsById(userIds: string) {
    return `${serverURL}/account/${this.getCountry()}/v1/list?accountIds=${userIds}`;
  }

  getOrdersBySimulation(stockId: string): string {
    return `${serverURL}/simulation/${this.getCountry()}/v1/orders/active?stockId=${stockId}`;
  }

  // getTransactionsBySimulation(stockId: string): string {
  //   return `${serverURL}/v1/simulation/transactions/active?stockId=${stockId}`;
  // }

  getTradeForSimulation(stockId: string): string {
    return `${serverURL}/simulation/${this.getCountry()}/v1/trade/active?stockId=${stockId}`;
  }

  getTransactionsForChart(simulationId: string): string {
    return `${serverURL}/simulation/${this.getCountry()}/v1/transactions?simulationId=${simulationId}`;
  }

  getStockById(stockId: string): string {
    return `${serverURL}/stock/${this.getCountry()}/v1/get?stockId=${stockId}`;
  }

  // getDailyTransactionByStockId(stockId: string): string {
  //   return `${serverURL}/v1/simulation/transactions/daily?stockId=${stockId}`;

  // }

  setDelay(stockId: string, delay: number): string {
    return `${serverURL}/simulation/${this.getCountry()}/delay?stockId=${stockId}&delay=${delay}`;
  }

  getAllStrategies() {
    return `${serverURL}/simulation/${this.getCountry()}/v1/strategies`;
  }

  //* USER API'S

  refreshUser(userId: string) {
    return `${serverURL}/user-context/${this.getCountry()}/v1/portfolio/refresh?userId=${userId}`;
  }

  getAllUsers() {
    return `${serverURL}/user/${this.getCountry()}/v1/list`;
  }

  getAllDecisionUsers() {
    return `${serverURL}/decision-log/${this.getCountry()}/v1/user-list`;
  }

  getAllDecisionStrategies() {
    return `${serverURL}/decision-log/${this.getCountry()}/v1/strategies`;
  }

  getAllSubscriptions(page: number, limit: number) {
    return `${serverURL}/subscription/${this.getCountry()}/v1/list?page=${page}&limit=${limit}`;
  }

  getAllRecommendationStrategies() {
    return `${serverURL}/recommendation-decision-log/${this.getCountry()}/v1/strategies`;
  }

  getAllRecommendationUsers() {
    return `${serverURL}/recommendation-decision-log/${this.getCountry()}/v1/user-list`;
  }

  getAllCompleteUsers() {
    return `${serverURL}/user/${this.getCountry()}/v1/list/complete`;
  }

  getAllInCompleteUsers() {
    return `${serverURL}/user/${this.getCountry()}/v1/list/incomplete`;
  }

  getAllUsersLite() {
    return `${serverURL}/user/${this.getCountry()}/v1/list/lite`;
  }

  getAllIciciUsers() {
    return `${serverURL}/user/${this.getCountry()}/v1/icici/list`;
  }

  getAllEmployee() {
    return `${serverURL}/user/${this.getCountry()}/v1/employee/list`;
  }

  /** DELETE: removes an employee from the employees page. */
  deleteEmployee(userId: string) {
    return `${serverURL}/user/${this.getCountry()}/v1/employee/${userId}`;
  }

  getPortFolio(userId: string) {
    return `${serverURL}/user-context/${this.getCountry()}/portfolio?userId=${userId}`;
  }

  getLoginUrlFromZerodha(userId: string) {
    return `${serverURL}/user-context/${this.getCountry()}/url?userId=${userId}`;
  }

  connectToZerodha() {
    return `${serverURL}/livetick/${this.getCountry()}/v1/session/url`;
  }

  submitTokenFromZerodha() {
    return `${serverURL}/user-context/${this.getCountry()}/create`;
  }

  getVerificationCode() {
    return `${serverURL}/user/${this.getCountry()}/v1/connect`;
  }

  verifyCode() {
    return `${serverURL}/user/${this.getCountry()}/v1/verify`;
  }

  saveUser() {
    return `${serverURL}/user/${this.getCountry()}/v1/admin/save`;
  }

  updateUser(userId: string) {
    return `${serverURL}/user/${this.getCountry()}/v1/update/${userId}`;
  }

  blockAndUnblockUser() {
    return `${serverURL}/${this.getCountry()}/v1/user/active`;
  }
  blockAndUnblockTradingAccount(userId: string, enabled: boolean) {
    return `${serverURL}/user-context/${this.getCountry()}/v1/admin/enable?userId=${userId}&enabled=${enabled}`;
  }

  //* ROLE AND PERMISSIONS API'S
  getAllRoles(admin: boolean): string {
    return `${serverURL}/role/${this.getCountry()}/v1/list?admin=${admin}`;
  }

  createRoles(): string {
    return `${serverURL}/role/${this.getCountry()}/v1/create`;
  }

  savePermissions(): string {
    return `${serverURL}/role/${this.getCountry()}/v1/save`;
  }

  refreshToken(token: string): string {
    return `${serverURL}/user/${this.getCountry()}/token?token=${encodeURI(token)}`;
  }

  getAllRole() {
    return `${serverURL}/role/${this.getCountry()}/list`;
  }
  getAllPermissions() {
    return `${serverURL}/role/${this.getCountry()}/permissions`;
  }

  createRole(roleId?: string) {
    return `${serverURL}/role/${this.getCountry()}/v1/save`;
  }

  //*ADMIN-LOG
  getAllAdminLogs(params: string) {
    return `${serverURL}/admin-log/${this.getCountry()}/v1/search?${params}`;
  }

  //*DECISION-LOG
  getAllDecisionLogs(params: string) {
    return `${serverURL}/decision-log/${this.getCountry()}/v1/search?${params}`;
  }

  getAllRecommendationLogs(params: string) {
    return `${serverURL}/recommendation-decision-log/${this.getCountry()}/v1/search?${params}`;
  }

  //*SETTINGS
  getAllSettings() {
    return `${serverURL}/settings/${this.getCountry()}/v1/list`;
  }
  getVersionSettings(platform: string, version: string) {
    return `${serverURL}/settings/${this.getCountry()}/v1/get?platform=${platform}&version=${version}`;
  }
  saveSettings() {
    return `${serverURL}/settings/${this.getCountry()}/v1/save`;
  }

  //* ORDER

  placeOrder() {
    return `${serverURL}/order/${this.getCountry()}/v1/save`;
  }

  modifyOrder() {
    return `${serverURL}/order/${this.getCountry()}/v1/save`;
  }

  cancelOrder(orderId: string) {
    return `${serverURL}/order/${this.getCountry()}/v1/cancel?orderId=${orderId}`;
  }

  reinitiateOrder(orderId: string) {
    return `${serverURL}/order/${this.getCountry()}/v1/reinitiate?orderId=${orderId}`;
  }

  getOrderGroup(page: PaginationState) {
    return `${serverURL}/order/${this.getCountry()}/v1/group?limit=${page.limit}&page=${page.page}`;
  }

  getDashboardOrders(orderStatus: string) {
    return `${serverURL}/order/${this.getCountry()}/v1/list?orderStatus=${orderStatus}`;
  }

  getNonDashboardOrders(orderStatus: string) {
    return `${serverURL}/order/${this.getCountry()}/v1/list?orderType=nonDashboardOrders&orderStatus=${orderStatus}`;
  }

  //* WATCH-LIST
  getAllWatchLists() {
    return `${serverURL}/watch-list/${this.getCountry()}/v1/all`;
  }

  saveWatchList() {
    return `${serverURL}/watch-list/${this.getCountry()}/v1/save`;
  }

  //* USER-GROUP
  getAllUserGroups() {
    return `${serverURL}/user-group/${this.getCountry()}/v1/all`;
  }

  createNewUserGroup() {
    return `${serverURL}/user-group/${this.getCountry()}/v1/save`;
  }

  //* KITE CONNECT FOR ADMIN

  getZerodhaLoginUrlAdmin() {
    return `${serverURL}/livetick/${this.getCountry()}/v1/url`;
  }

  createZerodhaConnection() {
    return `${serverURL}/livetick/${this.getCountry()}/v1/connect`;
  }

  submitAccessTokenToServer(accessToken: string, publicToken: string) {
    return `${serverURL}/livetick/${this.getCountry()}/v1/session/create?accessToken=${accessToken}&publicToken=${publicToken}`;
  }

  getZerodhaKiteConnectStatus() {
    return `${serverURL}/livetick/${this.getCountry()}/v1/status`;
  }

  getTickerSubscriptions() {
    return `${serverURL}/livetick/${this.getCountry()}/v1/subscriptions/ticker`;
  }

  getSyncFnoAPI() {
    return `${serverURL}/livetick/${this.getCountry()}/v1/fno/sync`;
  }

  getLastFnoSyncDateAPI() {
    return `${liveTickServerUrl}/livetick/${this.getCountry()}/v1/fno/last-sync`;
  }

  disconnectKite() {
    return `${serverURL}/livetick/${this.getCountry()}/v1/disconnect`;
  }

  getSubscriptions() {
    return `${serverURL}/livetick/${this.getCountry()}/v1/subscriptions`;
  }

  getSectorRanking(window: string) {
    return `${serverURL}/sector-performance/${this.getCountry()}/v1/ranking?window=${encodeURIComponent(window)}`;
  }

  refreshSectorRanking(window: string) {
    return `${serverURL}/sector-performance/${this.getCountry()}/v1/refresh?window=${encodeURIComponent(window)}`;
  }

  getSectorStocks(indexId: string, window: string) {
    return `${serverURL}/sector-performance/${this.getCountry()}/v1/stocks?indexId=${encodeURIComponent(
      indexId
    )}&window=${encodeURIComponent(window)}`;
  }

  getSectorTreemap(indexId: string, window: string) {
    return `${serverURL}/sector-performance/${this.getCountry()}/v1/treemap?indexId=${encodeURIComponent(
      indexId
    )}&window=${encodeURIComponent(window)}`;
  }

  resubscribeHoldings() {
    return `${serverURL}/livetick/${this.getCountry()}/v1/resubscribe`;
  }

  subscribeSelected() {
    return `${serverURL}/livetick/${this.getCountry()}/v1/subscribe-selected`;
  }

  //* MARKET DETAILS

  getMarketQuote(stockId: string) {
    return `${serverURL}/stock/${this.getCountry()}/v1/quote?stockId=${stockId}`;
  }

  getTickerTapeStockInfo(stockId: string) {
    return `${serverURL}/stock/${this.getCountry()}/v1/tickertape/info?stockId=${encodeURIComponent(stockId)}`;
  }

  getMarketQuoteByStockGroup(stockGroupId: string) {
    return `${serverURL}/stock/${this.getCountry()}/v1/quote/stock-group?stockGroupId=${stockGroupId}`;
  }

  subscribeToWatchList(stockId?: string) {
    if (stockId) {
      return `${serverURL}/stock/${this.getCountry()}/v1/subscribe?stockId=${stockId}`;
    }
    return `${serverURL}/stock/${this.getCountry()}/v1/subscribe?stockId=`;
  }

  //*NOTIFICATIONS
  getAllNotifications() {
    return `${serverURL}/notification/${this.getCountry()}/v1/all`;
  }

  //* SECTOR LIST

  getAllSectors() {
    return `${serverURL}/sector/${this.getCountry()}/v1/list`;
  }

  saveSector() {
    return `${serverURL}/sector/${this.getCountry()}/v1/save`;
  }

  updateSector(sectorId: string) {
    return `${serverURL}/sector/${this.getCountry()}/v1/update/${sectorId}`;
  }

  deleteSector(sectorId: string) {
    return `${serverURL}/sector/${this.getCountry()}/v1/delete?sectorId=${sectorId}`;
  }

  //* FUND LIST

  getAllFund() {
    return `${serverURL}/fund/${this.getCountry()}/v1/list`;
  }

  saveFund() {
    return `${serverURL}/fund/${this.getCountry()}/v1/save`;
  }

  updateFund(fundId: string) {
    return `${serverURL}/fund/${this.getCountry()}/v1/update/${fundId}`;
  }

  deleteFund(fundId: string) {
    return `${serverURL}/fund/${this.getCountry()}/v1/delete?fundId=${fundId}`;
  }

  getUserAccount(userId: string) {
    return `${serverURL}/user/${this.getCountry()}/v1/account?userId=${userId}`;
  }

  getUserAccountByIds(accountIds: string[]) {
    return `${serverURL}/account/${this.getCountry()}/v1/list?accountIds=${accountIds}`;
  }

  saveUserAccount() {
    return `${serverURL}/user-context/${this.getCountry()}/v1/account/save`;
  }

  saveUserAccountConfig(userId: string) {
    return `${serverURL}/user-context/${this.getCountry()}/v1/account/config/save?userId=${userId}`;
  }

  getAllOrders(page: number, limit: number) {
    return `${serverURL}/order/${this.getCountry()}/v1/list?page=${page}&limit=${limit}`;
  }

  getAllOrdersByUser(userId: string) {
    return `${serverURL}/orders/${this.getCountry()}/v1/user/list?userId=${userId}`;
  }

  disableSimulation(disabled: boolean, stockId: string, simulationId: string) {
    return `${serverURL}/simulation/${this.getCountry()}/v1/stock/disable?simulationId=${simulationId}&stockId=${stockId}&disabled=${disabled}`;
  }

  getAllPendingOrders() {
    return `${serverURL}/order/${this.getCountry()}/v1/pending/list`;
  }

  getAllPendingOrdersForRecommendation() {
    return `${serverURL}/order/${this.getCountry()}/v1/pending/recommendation/list`;
  }
  approveOrderRequest(requestId: string, simulationSource: SimulationSource) {
    return `${serverURL}/order/${this.getCountry()}/v1/pending/list/approve?requestId=${requestId}&simulationSource=${simulationSource}`;
  }

  declineOrderRequest(requestId: string, orderId: string, simulationId: string, userId?: string) {
    return `${serverURL}/order/${this.getCountry()}/v1/pending/list/decline?requestId=${requestId}&orderId=${orderId}&userId=${userId}&simulationId=${simulationId}`;
  }

  getUserRecommendations(userId?: string) {
    return `${serverURL}/recommendation/${this.getCountry()}/user/list?userId=${userId}`;
  }

  getAllSimulatonRecommendations(simulationId?: string) {
    return `${serverURL}/recommendation/${this.getCountry()}/v1/simulation/list?simulationId=${simulationId}`;
  }

  clearAllOrders(simulationSource: SimulationSource) {
    return `${serverURL}/order/${this.getCountry()}/v1/pending/list/clear?simulationSource=${simulationSource}`;
  }

  saveMarketHoliday() {
    return `${serverURL}/market-holiday/${this.getCountry()}/v1/save`;
  }

  updateMarketHoliday(holidayId: string) {
    return `${serverURL}/market-holiday/${this.getCountry()}/v1/update/${holidayId}`;
  }

  deleteMarketHoliday(holidayId: string) {
    return `${serverURL}/market-holiday/${this.getCountry()}/v1/delete/${holidayId}`;
  }

  listAllMarketHolidays() {
    return `${serverURL}/market-holiday/${this.getCountry()}/v1/list`;
  }

  getOptionChain() {
    return `${serverURL}/simulation/${this.getCountry()}/v1/get-option-chain`;
  }

  getCandle() {
    return `${serverURL}/simulation/${this.getCountry()}/v1/get-candle`;
  }

  prepareAndStoreIndicators() {
    return `${serverURL}/simulation/${this.getCountry()}/v1/prepareAndStore`;
  }

  fetchOptionData() {
    return `${serverURL}/historic-stock-price/${this.getCountry()}/save-option-data`;
  }

  fetchOptionOiData() {
    return `${serverURL}/historic-stock-price/${this.getCountry()}/save-oi-data`;
  }

  getStoredDataInfoForSimulation() {
    return `${serverURL}/historic-stock-price/${this.getCountry()}/v1/stored-candle-details`;
  }

  getStoredDataInfoForOption(stockId: string) {
    return `${serverURL}/historic-stock-price/${this.getCountry()}/v1/stored-option-details?stockId=${stockId}`;
  }

  getStoredDataInfoForOptionOi(stockId: string) {
    return `${serverURL}/historic-stock-price/${this.getCountry()}/v1/stored-option-oi-details?stockId=${stockId}`;
  }

  getQueueDataForSimulation(paginationState: PaginationState) {
    return `${serverURL}/historic-stock-price/${this.getCountry()}/stored-data-info/progress/simulation?page=${
      paginationState.page
    }&limit=${paginationState.limit}`;
  }

  getQueueDataForOption(paginationState: PaginationState) {
    return `${serverURL}/historic-stock-price/${this.getCountry()}/stored-data-info/progress/option?page=${paginationState.page}&limit=${
      paginationState.limit
    }`;
  }

  getQueueDataForOptionOi(paginationState: PaginationState) {
    return `${serverURL}/historic-stock-price/${this.getCountry()}/stored-data-info/progress/option-oi?page=${paginationState.page}&limit=${
      paginationState.limit
    }`;
  }

  getSimulatedStrategyNames() {
    return `${serverURL}/historic-stock-price/${this.getCountry()}/v1/strategy-names`;
  }

  deleteCandleDataByStrategy(strategyName: string) {
    return `${serverURL}/historic-stock-price/${this.getCountry()}/v1/delete/candle-data?strategyName=${encodeURIComponent(strategyName)}`;
  }

  deleteCandleData(scenario: string, stockId: string, date: string, configHash: string) {
    return `${serverURL}/historic-stock-price/${this.getCountry()}/v1/delete-candle-data?scenario=${scenario}&stockId=${stockId}&date=${date}&configHash=${configHash}`;
  }

  enableOrDisableTrade(enabled: boolean, userId: string) {
    return `${serverURL}/user-context/${this.getCountry()}/v1/admin/enable?enabled=${enabled}&userId=${userId}`;
  }

  clearUserContextById(userId: string) {
    return `${serverURL}/user-context/${this.getCountry()}/v1/clear/${userId}`;
  }

  clearUserContext() {
    return `${serverURL}/user-context/${this.getCountry()}/v1/clear`;
  }

  checkUserLoginStatus(userId: string) {
    return `${serverURL}/user/${this.getCountry()}/v1/login-status?userId=${userId}`;
  }

  getAllOrderPositions() {
    return `${serverURL}/account/${this.getCountry()}/v1/order-positions`;
  }

  createStrategy() {
    return `${serverURL}/strategy/${this.getCountry()}/v1/save`;
  }

  getStrategyById(id: string) {
    return `${serverURL}/strategy/${this.getCountry()}/v1/${id}`;
  }

  getAllStrategyUI() {
    return `${serverURL}/strategy/${this.getCountry()}/v1`;
  }

  getAllStrategyUILite() {
    return `${serverURL}/strategy/${this.getCountry()}/lite/v1`;
  }

  getAllBrokers() {
    return `${serverURL}/api/${this.getCountry()}/broker/v1/all`;
  }

  saveBroker() {
    return `${serverURL}/api/${this.getCountry()}/broker/v1/save`;
  }

  getEquities(page: PaginationState) {
    return `${serverURL}/stock/in/v1/list/equity?page=${page.page}&limit=${page.limit}`;
  }

  searchEquity(query: string, page: PaginationState) {
    return `${serverURL}/stock/in/v1/search/equity?query=${encodeURIComponent(query)}&page=${page.page}&limit=${page.limit}`;
  }

  uploadFnoInstruments() {
    return `${serverURL}/stock/in/v1/upload-instruments/fno`;
  }
  uploadEquityInstruments() {
    return `${serverURL}/stock/in/v1/upload-instruments/equity`;
  }
  autoFetchAllInstruments() {
    return `${serverURL}/stock/in/v1/upload-instruments/auto`;
  }
  getInstrumentsLastUpdated() {
    return `${serverURL}/stock/in/v1/upload-instruments/last-updated`;
  }
  updateFnoInstruments() {
    return `${serverURL}/stock/in/v1/update-instruments/fno`;
  }
  updateEquityInstruments() {
    return `${serverURL}/stock/in/v1/update-instruments/equity`;
  }

  getFno(page: PaginationState) {
    return `${serverURL}/stock/in/v1/list/fno?page=${page.page}&limit=${page.limit}`;
  }

  searchFno(query: string, page: PaginationState, broker?: string) {
    let url = `${serverURL}/stock/in/v1/search/fno?query=${encodeURIComponent(query)}&page=${page.page}&limit=${page.limit}`;
    if (broker) url += `&broker=${encodeURIComponent(broker)}`;
    return url;
  }

  getAllAccountsBySimulation(simulationId: string) {
    return `${serverURL}/account/${this.getCountry()}/v1/live/accounts?simulationId=${simulationId}`;
  }

  getOrderByUser(accountId: string, isSimulationAccount: boolean, simulationId: string) {
    return `${serverURL}/account/${this.getCountry()}/v1/account/orders?accountId=${accountId}&isSimulationAccount=${isSimulationAccount}&simulationId=${simulationId}`;
  }

  getPositions(accountId: string, isSimulationAccount: boolean, simulationId: string) {
    return `${serverURL}/account/${this.getCountry()}/v1/account/positions?accountId=${accountId}&isSimulationAccount=${isSimulationAccount}&simulationId=${simulationId}`;
  }

  getHoldings(accountId: string, isSimulationAccount: boolean, simulationId: string) {
    return `${serverURL}/account/${this.getCountry()}/v1/account/holdings?accountId=${accountId}&isSimulationAccount=${isSimulationAccount}&simulationId=${simulationId}`;
  }

  getTransactions(accountId: string, isSimulationAccount: boolean, simulationId: string) {
    return `${serverURL}/account/${this.getCountry()}/v1/account/passbook?accountId=${accountId}&isSimulationAccount=${isSimulationAccount}&simulationId=${simulationId}`;
  }

  getAccountConfig(accountId: string, isSimulationAccount = false, simulationId = '') {
    const simulationParams = isSimulationAccount ? `&isSimulationAccount=true&simulationId=${simulationId}` : '';
    return `${serverURL}/account/${this.getCountry()}/v1/account/config?accountId=${accountId}${simulationParams}`;
  }

  changeQuantity(requestId: string, quantity: number) {
    return `${serverURL}/order/${this.getCountry()}/v1/pending/change-quantity?requestId=${requestId}&quantity=${quantity}`;
  }

  editPendingOrder() {
    return `${serverURL}/order/${this.getCountry()}/v1/pending/edit`;
  }

  manualExecuteStrategyOrder() {
    return `${serverURL}/order/${this.getCountry()}/v1/strategy-order/manual-execute`;
  }

  editOrderPosition() {
    return `${serverURL}/order/${this.getCountry()}/v1/edit-order-position`;
  }
  getUserRecommendationMasterList(page: PaginationState, status?: string | null, userId?: string | null) {
    const params = new URLSearchParams();

    params.set('page', String(page.page));
    params.set('limit', String(page.limit));

    if (userId) {
      params.set('userId', userId);
    }

    if (status) {
      params.set('status', status);
    }

    const url = `${serverURL}/recommendation/${this.getCountry()}/v1/master/list?${params.toString()}`;
    return url;
  }

  getDeleteRequests(page: PaginationState, status?: string | null) {
    let url = `${serverURL}/admin/${this.getCountry()}/v1/delete-request?page=${page.page}&limit=${page.limit}`;
    if (status) url += `&status=${status}`;
    return url;
  }

  approveDeleteRequest() {
    return `${serverURL}/admin/${this.getCountry()}/v1/delete-request/accept`;
  }

  rejectDeleteRequest() {
    return `${serverURL}/admin/${this.getCountry()}/v1/delete-request/reject`;
  }

  presignUpload() {
    return `${serverURL}/files/${this.getCountry()}/v1/presign-upload`;
  }

  saveMarketEvent() {
    return `${serverURL}/market-event/${this.getCountry()}`;
  }

  updateMarketEvent(eventId: string) {
    return `${serverURL}/market-event/${this.getCountry()}/${eventId}`;
  }

  deleteMarketEvent(eventId: string) {
    return `${serverURL}/market-event/${this.getCountry()}/${eventId}`;
  }

  listAllMarketEvents() {
    return `${serverURL}/market-event/${this.getCountry()}`;
  }

  getDailyTasks() {
    return `${serverURL}/checklists/${this.getCountry()}/list/today`;
  }

  completeDailyTask(taskId: string, isDone: boolean) {
    return `${serverURL}/checklists/${this.getCountry()}/complete?taskId=${taskId}&isDone=${isDone}`;
  }

  checkTickServerhealth() {
    return `${liveTickServerUrl}/live-tick-server/${this.getCountry()}/health`;
  }

  getLiveTickCount() {
    return `${liveTickServerUrl}/live-tick-server/${this.getCountry()}/tick-count`;
  }

  getPortfoliioPositions(accountId: string) {
    return `${serverURL}/account/${this.getCountry()}/v1/portfolio-positions?accountId=${accountId}`;
  }

  getLiveTickCandle() {
    return `${liveTickServerUrl}/live-tick-server/${this.getCountry()}/candle-count`;
  }

  //* TICK STORE API'S
  // Historical candles pulled from Kite after the close, served by the main server.

  getTickStoreStatus() {
    return `${serverURL}/tickstore/${this.getCountry()}/v1/status`;
  }

  getTickStoreChain(params: { date: number; underlying?: string; expiry?: string; search?: string }) {
    const query = new URLSearchParams({ date: String(params.date) });
    if (params.underlying) query.append('underlying', params.underlying);
    if (params.expiry) query.append('expiry', params.expiry);
    if (params.search) query.append('search', params.search);
    return `${serverURL}/tickstore/${this.getCountry()}/v1/chain?${query.toString()}`;
  }

  postTickStorePull() {
    return `${serverURL}/tickstore/${this.getCountry()}/v1/pull`;
  }

  /** What the pull and archive did for one day, and whether a whole-day pull can run. */
  getTickStoreDay(date: number) {
    return `${serverURL}/tickstore/${this.getCountry()}/v1/day?date=${date}`;
  }

  /** POST: pull a whole missed day from Kite and archive it to S3, in the background. */
  postTickStoreDayPull(date: number) {
    return `${serverURL}/tickstore/${this.getCountry()}/v1/day/pull?date=${date}`;
  }

  getUserOrderPositions(accountId: string) {
    return `${serverURL}/account/${this.getCountry()}/v1/user/order-positions?accountId=${accountId}`;
  }

  //* MARKET CHARACTER API'S (FT-879)

  marketCharacterSources() {
    return `${serverURL}/market-character/${this.getCountry()}/v1/sources`;
  }

  marketCharacterFetch(stockId: string, startDate: string, endDate: string, timeframe: string) {
    return `${serverURL}/market-character/${this.getCountry()}/v1/fetch?stockId=${stockId}&startDate=${startDate}&endDate=${endDate}&timeframe=${timeframe}`;
  }

  marketCharacterAnalyzeDay(stockId: string, scenario: string, configHash: number, date: string, timeframe: string) {
    return `${serverURL}/market-character/${this.getCountry()}/v1/analyze-day?stockId=${stockId}&scenario=${encodeURIComponent(
      scenario
    )}&configHash=${configHash}&date=${date}&timeframe=${timeframe}`;
  }

  marketCharacterBackfill(stockId: string, scenario: string, configHash: number, startDate: string, endDate: string, timeframe: string) {
    return `${serverURL}/market-character/${this.getCountry()}/v1/backfill?stockId=${stockId}&scenario=${encodeURIComponent(
      scenario
    )}&configHash=${configHash}&startDate=${startDate}&endDate=${endDate}&timeframe=${timeframe}`;
  }

  marketCharacterLive(stockId: string, scenario: string, configHash: number, timeframe: string, asOf?: string) {
    let url = `${serverURL}/market-character/${this.getCountry()}/v1/live?stockId=${stockId}&scenario=${encodeURIComponent(
      scenario
    )}&configHash=${configHash}&timeframe=${timeframe}`;
    if (asOf) url += `&asOf=${asOf}`;
    return url;
  }

  marketCharacterLiveStart(stockId: string, scenario: string, configHash: number, timeframe: string) {
    return `${serverURL}/market-character/${this.getCountry()}/v1/live/start?stockId=${stockId}&scenario=${encodeURIComponent(
      scenario
    )}&configHash=${configHash}&timeframe=${timeframe}`;
  }

  marketCharacterLiveStop(stockId: string) {
    return `${serverURL}/market-character/${this.getCountry()}/v1/live/stop?stockId=${stockId}`;
  }

  marketCharacterLiveStatus() {
    return `${serverURL}/market-character/${this.getCountry()}/v1/live/status`;
  }

  marketCharacterLtp(stockId: string) {
    return `${serverURL}/market-character/${this.getCountry()}/v1/ltp?stockId=${stockId}`;
  }

  marketCharacterCalls(stockId: string, limit = 50) {
    return `${serverURL}/market-character/${this.getCountry()}/v1/calls?stockId=${stockId}&limit=${limit}`;
  }

  marketCharacterList(stockId: string, scenario: string, timeframe: string, startDate?: string, endDate?: string) {
    let url = `${serverURL}/market-character/${this.getCountry()}/v1/characters?stockId=${stockId}&scenario=${encodeURIComponent(
      scenario
    )}&timeframe=${timeframe}`;
    if (startDate && endDate) {
      url += `&startDate=${startDate}&endDate=${endDate}`;
    }
    return url;
  }

  //* CANDLE-SIGNATURE PATTERN MATCH API'S
  // Heikin-Ashi candle structure vs Bollinger bands, narrowed candle by candle.
  // Country must stay the second path segment — the server reads it positionally.

  private signatureBase() {
    return `${serverURL}/pattern-match/${this.getCountry()}/v1`;
  }

  /** Build (or refresh) the signature library. Omit dates for the default ~60-session window. */
  signatureLibraryBuild(stockId: string, timeframe: string, startDate?: string, endDate?: string) {
    let url = `${this.signatureBase()}/signature-library/build?stockId=${stockId}&timeframe=${timeframe}`;
    if (startDate) url += `&startDate=${startDate}`;
    if (endDate) url += `&endDate=${endDate}`;
    return url;
  }

  signatureLibraryCount(stockId: string, timeframe: string) {
    return `${this.signatureBase()}/signature-library/count?stockId=${stockId}&timeframe=${timeframe}`;
  }

  signatureLibraryList(stockId: string, timeframe: string, limit?: number) {
    let url = `${this.signatureBase()}/signature-library?stockId=${stockId}&timeframe=${timeframe}`;
    if (limit !== undefined) url += `&limit=${limit}`;
    return url;
  }

  signatureLibraryClear(stockId: string, timeframe: string) {
    return `${this.signatureBase()}/signature-library?stockId=${stockId}&timeframe=${timeframe}`;
  }

  /** The progressive funnel for today, or for a past session via asOf. */
  patternFunnel(stockId: string, timeframe: string, asOf?: string) {
    let url = `${this.signatureBase()}/funnel?stockId=${stockId}&timeframe=${timeframe}`;
    if (asOf) url += `&asOf=${asOf}`;
    return url;
  }

  patternFunnelNarrate(stockId: string, timeframe: string, asOf?: string) {
    let url = `${this.signatureBase()}/funnel/narrate?stockId=${stockId}&timeframe=${timeframe}`;
    if (asOf) url += `&asOf=${asOf}`;
    return url;
  }

  //* DAILY PATTERN MATCH V1 — separate engine, multiple stocks at once.

  private patternV1Base() {
    return `${serverURL}/pattern-v1/${this.getCountry()}/v1`;
  }

  /**
   * Stock and timeframe as an encoded query string.
   *
   * Encoding is not optional here: ids like M&M carry characters that are query syntax,
   * and interpolating one raw truncates the parameter — the server then looks up "M",
   * finds nothing, and answers 400.
   */
  private patternV1Params(stockId: string, timeframe: string) {
    return `stockId=${encodeURIComponent(stockId)}&timeframe=${encodeURIComponent(timeframe)}`;
  }

  /** Every stock currently being tracked — the list page. */
  patternV1Runs() {
    return `${this.patternV1Base()}/runs`;
  }

  /** `historyFrom` (dd-MMM-yyyy) is how far back the match library is filled. */
  patternV1Start(stockId: string, timeframe: string, historyFrom?: string) {
    let url = `${this.patternV1Base()}/runs/start?${this.patternV1Params(stockId, timeframe)}`;
    if (historyFrom) url += `&historyFrom=${encodeURIComponent(historyFrom)}`;
    return url;
  }

  /** Stops tracking, leaving the stock on the list so it can be started again in place. */
  patternV1Stop(stockId: string, timeframe: string) {
    return `${this.patternV1Base()}/runs/stop?${this.patternV1Params(stockId, timeframe)}`;
  }

  /** Takes the stock off the list for good. Stored candles and history are untouched. */
  patternV1Remove(stockId: string, timeframe: string) {
    return `${this.patternV1Base()}/runs/remove?${this.patternV1Params(stockId, timeframe)}`;
  }

  /** Live state for one running stock — candles, funnel, both match tiers. */
  patternV1Detail(stockId: string, timeframe: string) {
    return `${this.patternV1Base()}/detail?${this.patternV1Params(stockId, timeframe)}`;
  }

  /** Indicators a stock is matched on, and the toggle that changes them. */
  patternV1Indicators(stockId: string, timeframe: string) {
    return `${this.patternV1Base()}/indicators?${this.patternV1Params(stockId, timeframe)}`;
  }

  patternV1SetIndicator(stockId: string, timeframe: string, indicatorId: string, active: boolean) {
    return `${this.patternV1Base()}/indicators?${this.patternV1Params(stockId, timeframe)}&indicatorId=${indicatorId}&active=${active}`;
  }

  /** One page of past sessions for a stock, newest first. Returns { rows, total, page, size }. */
  patternV1History(stockId: string, timeframe: string, page = 0, size = 10) {
    return `${this.patternV1Base()}/history?${this.patternV1Params(stockId, timeframe)}&page=${page}&size=${size}`;
  }

  /** One past session in full, with the matches as they stood at the close. */
  patternV1HistoryDetail(stockId: string, timeframe: string, date: string) {
    return `${this.patternV1Base()}/history/detail?${this.patternV1Params(stockId, timeframe)}&date=${encodeURIComponent(date)}`;
  }

  patternV1LibraryCount(stockId: string, timeframe: string) {
    return `${this.patternV1Base()}/library/count?${this.patternV1Params(stockId, timeframe)}`;
  }

  /** Refills the match library from broker history. Slow — a long range is fetched a month at a time. */
  patternV1Backfill(stockId: string, timeframe: string, historyFrom?: string) {
    let url = `${this.patternV1Base()}/library/backfill?${this.patternV1Params(stockId, timeframe)}`;
    if (historyFrom) url += `&historyFrom=${encodeURIComponent(historyFrom)}`;
    return url;
  }

  /** Live last-traded price — cheap, polled to drive the forming candle. */
  patternLtp(stockId: string) {
    return `${this.signatureBase()}/ltp?stockId=${stockId}`;
  }

  /** Stored signatures for one session, for rendering a matched day. */
  daySignatures(stockId: string, timeframe: string, date: string) {
    return `${this.signatureBase()}/signatures?stockId=${stockId}&timeframe=${timeframe}&date=${date}`;
  }

  //* WEEKLY REPORT API'S
  // Reports are generated by the backend every Saturday for the week just ended
  // (Monday..Saturday). `weekStart` is always that week's Monday, as yyyy-MM-dd.

  /** Accounts that can have a weekly report, for the report screen's picker. */
  getReportAccounts() {
    return `${serverURL}/report/${this.getCountry()}/v1/weekly/accounts`;
  }

  /** Week starts (yyyy-MM-dd Mondays) that already have stored reports, newest first. */
  getReportWeeks(accountId?: string) {
    const url = `${serverURL}/report/${this.getCountry()}/v1/weekly/weeks`;
    return accountId ? `${url}?accountId=${accountId}` : url;
  }

  /** The stored weekly report for one account. 404 when it has not been generated yet. */
  getWeeklyReport(accountId: string, weekStart: string) {
    return `${serverURL}/report/${this.getCountry()}/v1/weekly?accountId=${accountId}&weekStart=${weekStart}`;
  }

  /** POST: recompute and re-store the report for one account/week. Returns the fresh report. */
  generateWeeklyReport(accountId: string, weekStart: string) {
    return `${serverURL}/report/${this.getCountry()}/v1/weekly/generate?accountId=${accountId}&weekStart=${weekStart}`;
  }

  //* DAILY REPORT API'S
  // Stored by the backend at 16:15 on trading days, one per user account.
  // `date` is yyyy-MM-dd (IST).

  /** Days that have stored reports, newest first. */
  getDailyReportDates() {
    return `${serverURL}/report/${this.getCountry()}/v1/daily/dates`;
  }

  /** Every account's report for one day. */
  getDailyReports(date: string) {
    return `${serverURL}/report/${this.getCountry()}/v1/daily/list?date=${date}`;
  }

  /** One account's report for one day. 404 when it has not been generated. */
  getDailyReport(accountId: string, date: string) {
    return `${serverURL}/report/${this.getCountry()}/v1/daily?accountId=${accountId}&date=${date}`;
  }

  /** POST: rebuild today's reports — one account when accountId is given, otherwise all. */
  generateDailyReports(accountId?: string) {
    const url = `${serverURL}/report/${this.getCountry()}/v1/daily/generate`;
    return accountId ? `${url}?accountId=${accountId}` : url;
  }
}

const api = new API();
export default api;
